import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { BiUpload, BiX, BiFile } from 'react-icons/bi'
import { useTranslations } from '../../translations'
import { extractTextFromFile } from '../utils/fileTextExtractor'
import Swal from 'sweetalert2'

const FileUpload = ({ onTextExtracted, disabled }) => {
  const { t } = useTranslations()
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isExtracting, setIsExtracting] = useState(false)

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]
      setUploadedFile(file)
      setIsExtracting(true)

      try {
        const extractedText = await extractTextFromFile(file)
        onTextExtracted(extractedText)
        
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: `${t('uploadedFile')}: ${file.name}`,
          timer: 2000,
          showConfirmButton: false,
        })
      } catch (error) {
        console.error('Error extracting text:', error)
        
        // Get the error message key
        const errorKey = error.message || 'fileUploadError'
        
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: t(errorKey),
          confirmButtonText: t('accept'),
        })
        
        setUploadedFile(null)
      } finally {
        setIsExtracting(false)
      }
    },
    [onTextExtracted, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    disabled: disabled || isExtracting,
  })

  const handleRemoveFile = () => {
    setUploadedFile(null)
    onTextExtracted('')
  }

  return (
    <div className="mb-4">
      {!uploadedFile ? (
        <div
          {...getRootProps()}
          className={`modern-upload-zone p-5 text-center transition-all ${
            isDragActive ? 'active' : ''
          } ${disabled || isExtracting ? 'disabled' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="upload-icon-wrapper mb-3 mx-auto shadow-sm">
            <BiUpload size={32} className="text-pink" />
          </div>
          <p className="mb-2 fw-bold color-plum fs-5">
            {isExtracting ? t('extractingText') : t('uploadFile')}
          </p>
          <p className="mb-3 text-muted px-4">
            {t('dragDropFile')}
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <span className="badge rounded-pill bg-light text-muted border px-3 py-2 small">PDF</span>
            <span className="badge rounded-pill bg-light text-muted border px-3 py-2 small">DOCX</span>
          </div>
          <small className="text-muted d-block mt-3 opacity-75">{t('fileSizeLimit')} (10MB)</small>
        </div>
      ) : (
        <div className="modern-file-card p-4 animate__animated animate__fadeIn">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div className="file-icon-wrapper me-3 shadow-sm">
                <BiFile size={28} className="text-pink" />
              </div>
              <div>
                <p className="mb-0 fw-bold color-plum">{uploadedFile.name}</p>
                <small className="text-muted fw-medium">
                  {(uploadedFile.size / 1024).toFixed(2)} KB
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border-0 text-danger rounded-circle p-2 hover-shadow transition-all"
              onClick={handleRemoveFile}
              disabled={disabled}
              title={t('removeFile')}
            >
              <BiX size={24} />
            </button>
          </div>
          {isExtracting && (
            <div className="mt-3">
              <div className="progress modern-progress" style={{ height: '6px' }}>
                <div className="progress-bar progress-bar-striped progress-bar-animated bg-pink w-100"></div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .modern-upload-zone {
          border: 2px dashed #e0e0e0;
          border-radius: 20px;
          cursor: pointer;
          background-color: #fafafa;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .modern-upload-zone:hover {
          border-color: var(--pink-bright);
          background-color: rgba(235, 33, 120, 0.02);
          transform: translateY(-2px);
        }
        .modern-upload-zone.active {
          border-color: var(--pink-bright);
          background-color: rgba(235, 33, 120, 0.05);
          box-shadow: 0 10px 25px -5px rgba(235, 33, 120, 0.1);
        }
        .modern-upload-zone.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        }
        .upload-icon-wrapper, .file-icon-wrapper {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border-radius: 16px;
        }
        .modern-file-card {
          background: white;
          border-radius: 20px;
          border: 1px solid rgba(0,0,0,0.05);
          box-shadow: 0 4px 20px rgba(0,0,0,0.04);
        }
        .bg-pink { background-color: var(--pink-bright); }
        .color-plum { color: var(--plum-bg); }
        .text-pink { color: var(--pink-bright); }
      `}</style>
    </div>
  )
}

export default FileUpload
